import type { QueryClient } from "@tanstack/react-query";
import type { AnyRouter, useNavigate } from "@tanstack/react-router";
import { uploadImageApiImagesUploadPost } from "../generated/api/sdk.gen";
import { getErrorMessage } from "./api-client";
import { invalidateGallerySessions } from "./queries";

export interface RunRefineUploadParams {
  file: File | null;
  projectId: string | undefined;
  navigate: ReturnType<typeof useNavigate>;
  queryClient: QueryClient;
  router: AnyRouter;
  setIsUploading: (v: boolean) => void;
  setError: (err: string | null) => void;
}

export async function runRefineUpload({
  file,
  projectId,
  navigate,
  queryClient,
  router,
  setIsUploading,
  setError,
}: RunRefineUploadParams): Promise<void> {
  if (!file) return;
  setIsUploading(true);
  setError(null);
  try {
    const { data } = await uploadImageApiImagesUploadPost({
      body: { image: file, project_id: projectId ?? null },
    });
    // An upload is the root of a new session, so the gallery is stale from
    // this point on — refresh it before leaving, like every other flow that
    // creates images does.
    await invalidateGallerySessions(queryClient, router);
    // isUploading stays true: navigation unmounts the page.
    navigate({
      to: "/refine",
      search: { imageId: data.id, sessionId: data.session_id },
    });
  } catch (err) {
    setError(getErrorMessage(err));
    setIsUploading(false);
  }
}
